import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Code, TestTube } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguageNavigation } from "@/components/LanguageRouter";

type HighlightedTool = "kindly" | "lad" | "tdd";

const tools = [
  { key: "kindly", icon: Search, path: "/open-source/kindly-web-search" },
  { key: "lad", icon: Code, path: "/open-source/lad-code-review" },
  { key: "tdd", icon: TestTube, path: "/open-source/tdd" },
] as const;

export function SuiteIntegrationSection({ highlight }: { highlight: HighlightedTool }) {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();

  return (
    <section className="py-16 md:py-20 bg-muted">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {t("tddSkill.integration.title")}
          </h2>
          <p className="text-muted-foreground mb-8">
            {t("tddSkill.integration.description")}
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {tools.map(({ key, icon: Icon, path }) => {
              const isHighlighted = key === highlight;
              const card = (
                <Card
                  key={key}
                  className={`transition-colors h-full ${
                    isHighlighted
                      ? "border-primary/30 bg-primary/5"
                      : "border-border/50 hover:border-primary/20 hover:bg-primary/5 cursor-pointer"
                  }`}
                >
                  <CardContent className="pt-6 text-center h-full flex flex-col items-center justify-start">
                    <div
                      className={`flex h-12 w-12 mx-auto items-center justify-center rounded-xl mb-4 ${
                        isHighlighted
                          ? "bg-primary text-primary-foreground"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">
                      {t(`tddSkill.integration.${key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(`tddSkill.integration.${key}.description`)}
                    </p>
                  </CardContent>
                </Card>
              );

              if (isHighlighted) return <div key={key}>{card}</div>;

              return (
                <Link key={key} to={getLocalizedPath(path)} className="no-underline">
                  {card}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
